// Every number the solver and the group layer argue with, in one place.
//
// The spec is explicit that these are configuration tuned against real preference,
// not constants that happen to be right. They are exposed in the developer panel
// and overridable per workspace, so nothing downstream may hardcode a copy.

export interface TuningConfig {
  /** Partial attendance: below this fraction of a set, value collapses toward zero. */
  fractionFloor: number;
  /** Smoothness of that collapse. A cliff makes the solver oscillate at the boundary. */
  fractionSoftness: number;
  /** The same floor expressed in absolute minutes, applied alongside the fraction. */
  minutesFloor: number;
  minutesSoftness: number;
  /** Weight of each third of a set. Sets are built to peak late. */
  positionWeights: { opening: number; middle: number; closing: number };

  /** bonus(r) = rankBonusB * rankBonusLambda^(r-1), added to a top-ten artist's utility. */
  rankBonusB: number;
  rankBonusLambda: number;

  /** A seeded rating contributes at this fraction of a manual one's weight. */
  seededConfidenceDiscount: number;

  /** weight = 1 + alpha * (decayedRegret / meanDecayedRegret), clamped. */
  ledgerAlpha: number;
  ledgerWeightClamp: [number, number];
  /** Multiplier applied to accumulated regret once per festival day. */
  ledgerDailyDecay: number;

  /** severity = spread * reach^reachExponent * lockFactor * durationFactor */
  severityReachExponent: number;
  severityLockFactor: number;
  severityLockCollisionFactor: number;
  /** Contested windows shorter than this are not worth a card. Milliseconds. */
  severityMinWindowMs: number;
  severityDurationHalfLifeMs: number;

  /** Objective weights per onboarding answer Q2. */
  goalWeights: Record<string, { music: number; together: number; rest: number; discovery: number }>;

  /** Stamina: required rest minutes per night and the decay applied to late utility. */
  staminaProfiles: Record<string, { restMinutesPerNight: number; nightlyDecay: number }>;

  /** Seeding: score given to an artist whose tags match a subgenre at each weight. */
  seedScores: { love: number; like: number; no: number; unmatched: number };
  seedConfidence: number;

  /** Defaults a workspace starts with. */
  hardLockCap: number;
  tokenAllocation: number;

  /** A set must be at least this many minutes to be worth a solver breakpoint pair. */
  minSetMinutes: number;
}

export const DEFAULT_CONFIG: TuningConfig = {
  fractionFloor: 0.2,
  fractionSoftness: 0.05,
  minutesFloor: 15,
  minutesSoftness: 4,
  positionWeights: { opening: 0.85, middle: 1.0, closing: 1.2 },

  rankBonusB: 0.22,
  rankBonusLambda: 0.85,

  seededConfidenceDiscount: 0.4,

  ledgerAlpha: 0.5,
  ledgerWeightClamp: [1.0, 2.0],
  ledgerDailyDecay: 0.7,

  severityReachExponent: 0.6,
  severityLockFactor: 1.6,
  severityLockCollisionFactor: 2.4,
  severityMinWindowMs: 10 * 60 * 1000,
  severityDurationHalfLifeMs: 45 * 60 * 1000,

  goalWeights: {
    max_music: { music: 1.0, together: 0.2, rest: 0.15, discovery: 0.3 },
    stay_together: { music: 0.7, together: 1.0, rest: 0.3, discovery: 0.2 },
    headliners_and_rest: { music: 0.8, together: 0.4, rest: 1.0, discovery: 0.15 },
    discover: { music: 0.85, together: 0.25, rest: 0.3, discovery: 1.0 },
  },

  staminaProfiles: {
    all_three_nights: { restMinutesPerNight: 45, nightlyDecay: 1.0 },
    taper: { restMinutesPerNight: 90, nightlyDecay: 0.9 },
    protect_one_night: { restMinutesPerNight: 120, nightlyDecay: 0.82 },
  },

  seedScores: { love: 7.5, like: 6.0, no: 3.0, unmatched: 4.5 },
  seedConfidence: 0.25,

  hardLockCap: 3,
  tokenAllocation: 3,

  minSetMinutes: 5,
};

/** Shallow merge, so a developer panel can override one curve without restating the rest. */
export function withOverrides(base: TuningConfig, over: Partial<TuningConfig>): TuningConfig {
  return { ...base, ...over };
}
